import { createContext, useContext, useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { CalendarDays, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { api, formatApiErrorDetail } from "@/lib/api";
import { MEAL_TIMES, EXPERIENCE_OPTIONS } from "@/data/content";
import { useContent } from "@/context/ContentContext";

const BookingContext = createContext(null);
export const useBooking = () => useContext(BookingContext);

const inputCls =
  "rounded-none border-[#ddd6c8] bg-white text-[#23281f] placeholder:text-[#9a9488] focus-visible:ring-[#ba5a45] focus-visible:ring-1 focus-visible:border-[#ba5a45] h-11";

export function BookingProvider({ children }) {
  const { content } = useContent();
  const ADVENTURE_OPTIONS = [
    ...new Set([...EXPERIENCE_OPTIONS, ...content.adventures.map((a) => a.name)]),
  ];
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("tour"); // "tour" | "cafe"
  const [submitting, setSubmitting] = useState(false);
  const [date, setDate] = useState(null);
  const [form, setForm] = useState({});

  const openTour = (adventure) => {
    setMode("tour");
    setForm({ adventure: adventure || ADVENTURE_OPTIONS[0], group_size: "2" });
    setDate(null);
    setOpen(true);
  };
  const openCafe = () => {
    setMode("cafe");
    setForm({ meal: "Dinner", party_size: "2", time: MEAL_TIMES.Dinner[2] });
    setDate(null);
    setOpen(true);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (mode === "tour") {
        await api.post("/tour-bookings", {
          name: form.name,
          email: form.email,
          phone: form.phone,
          adventure: form.adventure,
          preferred_date: date ? format(date, "yyyy-MM-dd") : null,
          group_size: parseInt(form.group_size || "1", 10),
          message: form.message || "",
        });
        toast.success("Enquiry received", {
          description: "We'll reach out shortly to confirm your adventure.",
        });
      } else {
        if (!date) {
          toast.error("Please select a date");
          setSubmitting(false);
          return;
        }
        await api.post("/cafe-reservations", {
          name: form.name,
          email: form.email,
          phone: form.phone,
          date: format(date, "yyyy-MM-dd"),
          meal: form.meal,
          time: form.time,
          party_size: parseInt(form.party_size || "2", 10),
          message: form.message || "",
        });
        toast.success("Table requested", {
          description: "A warm table in the wild — we'll confirm shortly.",
        });
      }
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Please try again");
    } finally {
      setSubmitting(false);
    }
  };

  const isTour = mode === "tour";

  return (
    <BookingContext.Provider value={{ openTour, openCafe }}>
      {children}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid="booking-dialog"
          className="max-w-lg rounded-none border-[#ddd6c8] bg-[#f4f1ea] text-[#23281f] max-h-[92vh] overflow-y-auto"
        >
          <DialogHeader>
            <div className="overline mb-2">{isTour ? "Plan your journey" : "Aruha Cafe & Bistro"}</div>
            <DialogTitle className="font-serif text-3xl font-medium tracking-tight">
              {isTour ? "Enquire about an experience" : "Reserve a table"}
            </DialogTitle>
            <DialogDescription className="text-[#5c6157]">
              {isTour
                ? "Share a few details and our mountain team will craft your trip."
                : "Lunch or dinner in the heart of Govind Wildlife Sanctuary."}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={submit} className="space-y-4 pt-2">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Full name">
                <Input
                  data-testid="booking-name"
                  required
                  className={inputCls}
                  value={form.name || ""}
                  onChange={(e) => set("name", e.target.value)}
                  placeholder="Your name"
                />
              </Field>
              <Field label="Phone">
                <Input
                  data-testid="booking-phone"
                  required
                  className={inputCls}
                  value={form.phone || ""}
                  onChange={(e) => set("phone", e.target.value)}
                  placeholder="+91 ..."
                />
              </Field>
            </div>

            <Field label="Email">
              <Input
                data-testid="booking-email"
                required
                type="email"
                className={inputCls}
                value={form.email || ""}
                onChange={(e) => set("email", e.target.value)}
                placeholder="you@email.com"
              />
            </Field>

            {isTour ? (
              <>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Experience">
                    <Select value={form.adventure} onValueChange={(v) => set("adventure", v)}>
                      <SelectTrigger data-testid="booking-adventure" className={inputCls}>
                        <SelectValue placeholder="Choose" />
                      </SelectTrigger>
                      <SelectContent className="rounded-none border-[#ddd6c8] bg-white text-[#23281f]">
                        {[...new Set([form.adventure, ...ADVENTURE_OPTIONS].filter(Boolean))].map((a) => (
                          <SelectItem key={a} value={a} className="focus:bg-[#ba5a45]/10 focus:text-[#23281f]">
                            {a}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Group size">
                    <Select value={form.group_size} onValueChange={(v) => set("group_size", v)}>
                      <SelectTrigger data-testid="booking-group-size" className={inputCls}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-none border-[#ddd6c8] bg-white text-[#23281f]">
                        {[1, 2, 3, 4, 5, 6, 8, 10, 12].map((n) => (
                          <SelectItem key={n} value={String(n)} className="focus:bg-[#ba5a45]/10 focus:text-[#23281f]">
                            {n} {n === 1 ? "person" : "people"}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
                <Field label="Preferred date (optional)">
                  <DatePicker date={date} setDate={setDate} testId="booking-date" />
                </Field>
              </>
            ) : (
              <>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Meal">
                    <Select
                      value={form.meal}
                      onValueChange={(v) => set("meal", v) || set("time", MEAL_TIMES[v][2])}
                    >
                      <SelectTrigger data-testid="booking-meal" className={inputCls}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-none border-[#ddd6c8] bg-white text-[#23281f]">
                        {["Lunch", "Dinner"].map((m) => (
                          <SelectItem key={m} value={m} className="focus:bg-[#ba5a45]/10 focus:text-[#23281f]">
                            {m}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Party size">
                    <Select value={form.party_size} onValueChange={(v) => set("party_size", v)}>
                      <SelectTrigger data-testid="booking-party-size" className={inputCls}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-none border-[#ddd6c8] bg-white text-[#23281f]">
                        {[1, 2, 3, 4, 5, 6, 8, 10].map((n) => (
                          <SelectItem key={n} value={String(n)} className="focus:bg-[#ba5a45]/10 focus:text-[#23281f]">
                            {n} {n === 1 ? "guest" : "guests"}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Date">
                    <DatePicker date={date} setDate={setDate} testId="booking-date" />
                  </Field>
                  <Field label="Time">
                    <Select value={form.time} onValueChange={(v) => set("time", v)}>
                      <SelectTrigger data-testid="booking-time" className={inputCls}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-none border-[#ddd6c8] bg-white text-[#23281f]">
                        {(MEAL_TIMES[form.meal] || MEAL_TIMES.Dinner).map((t) => (
                          <SelectItem key={t} value={t} className="focus:bg-[#ba5a45]/10 focus:text-[#23281f]">
                            {t}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
              </>
            )}

            <Field label="Message (optional)">
              <Textarea
                data-testid="booking-message"
                className={`${inputCls} h-24`}
                value={form.message || ""}
                onChange={(e) => set("message", e.target.value)}
                placeholder="Anything we should know?"
              />
            </Field>

            <Button
              data-testid="booking-submit"
              type="submit"
              disabled={submitting}
              className="w-full h-12 rounded-none bg-[#ba5a45] text-[#f2efeb] hover:bg-[#9c4633] tracking-wide uppercase text-xs font-semibold transition-colors"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : isTour ? "Send enquiry" : "Request table"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </BookingContext.Provider>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-[11px] uppercase tracking-[0.18em] text-[#5c6157]">{label}</Label>
    {children}
  </div>
);

const DatePicker = ({ date, setDate, testId }) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          data-testid={testId}
          type="button"
          variant="outline"
          className={`${inputCls} w-full justify-start font-normal hover:bg-white hover:text-[#23281f]`}
        >
          <CalendarDays className="mr-2 h-4 w-4 text-[#ba5a45]" />
          {date ? format(date, "PPP") : <span className="text-[#9a9488]">Pick a date</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 rounded-none border-[#ddd6c8] bg-white" align="start">
        <Calendar
          mode="single"
          selected={date}
          onSelect={(d) => {
            setDate(d);
            setOpen(false);
          }}
          disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
          initialFocus
          className="text-[#23281f]"
        />
      </PopoverContent>
    </Popover>
  );
};
